import { useMemo } from 'react'

import type { SpaceMember } from '@tribeplatform/gql-client/types'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useSpaceMembers } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'

import { convertToStatic } from '../../Views/utils'
import { staticFieldsFactory } from '../config'
import { useCustomFields } from '../hooks/useCustomFields'
import { useShowMoreButton } from '../hooks/useShowMoreButton'
import type { MembersBlockView } from '../types'
import { EmptyState } from './EmptyState'
import { GenericMembersList } from './GenericMembersList'
import { LoadingState } from './LoadingState'

interface Props {
  view: MembersBlockView
  activeFieldId: string
}
export const SpaceMemberList = ({
  view: {
    spaceId,
    roleIds,
    limit,
    style,
    fields,
    gallerySize,
    showDivider,
    showMore,
  },
  activeFieldId,
}: Props) => {
  const staticFields = useStaticIntl(staticFieldsFactory)
  const { memberFields } = useCustomFields()
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSpaceMembers({
      fields: {
        member: {
          profilePicture: 'basic',
          role: 'basic',
          badges: 'default',
          fields: 'basic',
          authMemberProps: 'basic',
        },
        role: 'basic',
      },
      variables: { limit, spaceId, roleIds },
      useInfiniteQueryOptions: {
        enabled: !!spaceId,
        refetchOnMount: 'always',
      },
    })
  const { nodes: spaceMembers } = simplifyPaginatedResult<SpaceMember>(data)

  const showMoreButton = useShowMoreButton({
    style,
    gallerySize,
    showMore,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  })

  const members = useMemo(
    () =>
      spaceMembers
        .filter(
          ({ role }) =>
            !roleIds || (roleIds?.length > 0 && roleIds.includes(role.id)),
        )
        .map(({ member }) => member),
    [roleIds, spaceMembers],
  )

  if (!isLoading && data && members && members.length === 0) {
    return <EmptyState />
  }

  if (isLoading || !data) {
    return (
      <LoadingState
        count={limit}
        style={style}
        gallerySize={gallerySize}
        showDivider={showDivider}
      />
    )
  }

  return (
    <>
      <GenericMembersList
        style={style}
        members={members}
        fields={convertToStatic(fields, staticFields, memberFields ?? [])}
        gallerySize={gallerySize}
        showDivider={showDivider}
        activeFieldId={activeFieldId}
        showMoreButton={showMoreButton}
      />
    </>
  )
}
